import { isRegionProvider, type Provider } from '@latency-globe/shared';
import type { Contribute, ContributeState } from '../contribute.ts';
import type { Store } from '../store.ts';
import { clear, el, PROVIDER_LABEL } from './dom.ts';

/**
 * SPEC §9.2 — the label and the one-line disclosure change with the provider,
 * and both appear *before* the geolocation prompt. Both do nothing if the
 * answer is no.
 */
const LABEL = (p: Provider) => (isRegionProvider(p) ? 'Measure my nearest region' : 'Put me on the map');
const DISCLOSURE = (p: Provider) => isRegionProvider(p)
  ? `~60–70 requests to ${PROVIDER_LABEL[p]} over 5–10 seconds, from your browser. We record the winner, which Cloudflare location served you, and a ~5 km grid square — never your exact position.`
  : 'One request. We record which Cloudflare location served you, and a ~5 km grid square — never your exact position.';

export function mountContribute(root: HTMLElement, store: Store, flow: Contribute, opts: { available: boolean; onSubmitted: (s: Extract<ContributeState, { kind: 'submitted' }>) => void }): void {
  const render = () => {
    clear(root);
    const p = store.state.provider;
    const s = flow.state;
    if (!opts.available) return;
    switch (s.kind) {
      case 'idle':
      case 'inconclusive':
      case 'failed': {
        const b = el('button', { class: 'btn', type: 'button', text: LABEL(p) });
        b.addEventListener('click', () => void flow.start(p));
        root.append(b, el('p', { class: 'disclosure', text: DISCLOSURE(p) }));
        if (s.kind === 'inconclusive') root.append(el('p', { class: 'status', text: {
          all_failed: 'Every probe failed: this usually means a blocker or a captive portal. Nothing was sent.',
          skipped_connection: 'Your connection is metered or very slow, so the measurement was skipped. Nothing was sent.',
        }[s.reason] }));
        if (s.kind === 'failed') root.append(el('p', { class: 'status danger', text: s.message }));
        break;
      }
      case 'permission':
        root.append(el('p', { class: 'status', text: 'Waiting for your location permission…' }), cancel());
        break;
      case 'probing': {
        const bar = el('div', { class: 'progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': String(s.progress.total), 'aria-valuenow': String(s.progress.done) });
        const fill = el('div'); fill.style.width = `${Math.min(100, (100 * s.progress.done) / s.progress.total)}%`; bar.append(fill);
        root.append(el('p', { class: 'status', text: `Measuring ${PROVIDER_LABEL[s.provider]} regions… ${s.progress.done}/${s.progress.total} (${s.progress.phase})` }), bar, cancel());
        break;
      }
      case 'submitting':
        root.append(el('p', { class: 'status', text: 'Recording…' }));
        break;
      case 'submitted': {
        const r = s.result;
        const loc = store.locationsFor(s.provider);
        const text = r.accepted
          ? isRegionProvider(s.provider)
            ? `Recorded: ${loc.get(s.code!)?.name ?? s.code} won for ${PROVIDER_LABEL[s.provider]} from ${s.cell}${s.rttMs ? ` (${Math.round(s.rttMs)} ms)` : ''}. You were served by Cloudflare ${r.name ? `${r.name} (${r.colo})` : r.colo}.`
            : `You were served by ${r.name ? `${r.name} (${r.colo})` : r.colo}. Your cell ${s.cell} joins the map at the next rebuild (every 15 minutes).`
          : r.reason === 'cell_saturated'
            ? 'Thanks — this cell has had plenty of visits this hour, so yours was not counted.'
            : 'Thanks — this visit could not be recorded.';
        root.append(el('p', { class: 'status', text }));
        break;
      }
      case 'declined':
        root.append(el('p', { class: 'status', text: 'Measuring needs a location, so nothing was collected. The map works fine without one.' }));
        break;
    }
  };
  const cancel = () => { const b = el('button', { class: 'btn secondary', type: 'button', text: 'Cancel' }); b.addEventListener('click', () => flow.cancel()); return b; };
  render();
  store.subscribe((_s, changed) => { if (changed.has('provider') || changed.has('locations')) render(); });
  flow.subscribe((s) => { render(); if (s.kind === 'submitted') opts.onSubmitted(s); });
}
